import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";
import { DEFAULT_TRANSLATION, EMPTY_STREAKS, isTranslationCode, localDayKey, recordCompletion, type Streaks, type TranslationCode } from "@versedrill/core";
import { addPlayedDay, seedPlayedDays } from "./activity.ts";

export type ThemeChoice = "system" | "light" | "dark";

/** A verse this player sent to someone (kept so they can see how many people found it). */
export interface SentRecord {
  code: string;
  createdAt: number;
  /** Shown in "My sent verses", e.g. "Ruth 1:16". The sender chose it, so it's no spoiler for them. */
  reference: string;
  /** Secret that lets this browser retract the verse. Never shown or sent anywhere except the retract call. */
  manageToken: string;
}

interface Persisted {
  /** Stars earned per game number (1–5). Presence means "played". */
  stars: Record<number, number>;
  streaks: Streaks;
  seenIntro: boolean;
  theme: ThemeChoice;
  /** Anonymous, random, per-browser. Lets the server count one solve/report per person without accounts. */
  playerId: string | null;
  sent: SentRecord[];
  /** Remembered so the next verse is quicker to send. */
  senderName: string;
  /** Stars for verses other people sent me, by code. */
  received: Record<string, number>;
  /** Date of the newest changelog entry this player has looked at (null: never). Drives the "new" dot. */
  changelogSeen: string | null;
  /** The Bible version the player reads the game in. */
  translation: TranslationCode;
  /** The player said "Not now" to the install card on the result screen. It never comes back on its own. */
  installCardDismissed: boolean;
  /** The one-time "it's an app now" pop-up has been shown (and closed). */
  installIntroSeen: boolean;
  /** Daily reminder: whether this device asked for one, and the local hour. (The server holds the rest.) */
  reminder: { on: boolean; hour: number };
  /** Local days (YYYY-MM-DD) the player finished a game, oldest first. Feeds the week row and the activity grid. */
  playedDays: string[];
  /** Cross-device sync. `code` is the secret that ties devices together; null = this device doesn't sync. */
  sync: { code: string | null; version: number; lastSyncedAt: number | null; localChangedAt: number };
}

interface Store extends Persisted {
  recordResult: (number: number, stars: number, now?: Date) => void;
  recordReceived: (code: string, stars: number, now?: Date) => void;
  addSent: (rec: SentRecord) => void;
  forgetSent: (code: string) => void;
  setSenderName: (name: string) => void;
  getPlayerId: () => string;
  markIntroSeen: () => void;
  markChangelogSeen: (date: string) => void;
  setTranslation: (t: TranslationCode) => void;
  dismissInstallCard: () => void;
  markInstallIntroSeen: () => void;
  setReminder: (r: Partial<{ on: boolean; hour: number }>) => void;
  setSync: (s: Partial<Persisted["sync"]>) => void;
  setTheme: (t: ThemeChoice) => void;
}

const MAX_SENT_KEPT = 50;

/** Older saved data (v1 had only stars/streaks/theme) gains the new fields; nothing a player earned is touched. */
export function migratePersisted(persisted: unknown): Persisted {
  return { playerId: null, sent: [], senderName: "", received: {}, changelogSeen: null, translation: DEFAULT_TRANSLATION, installCardDismissed: false, installIntroSeen: false, reminder: { on: false, hour: 8 }, playedDays: [], sync: { code: null, version: 0, lastSyncedAt: null, localChangedAt: 0 }, ...(persisted as object) } as unknown as Persisted;
}

/** localStorage can throw (private windows, blocked site data). The app must still work without it. */
const safeStorage: StateStorage = {
  getItem: (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* progress just won't persist */
    }
  },
  removeItem: (k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      stars: {},
      streaks: EMPTY_STREAKS,
      seenIntro: false,
      theme: "system",
      playerId: null,
      sent: [],
      senderName: "",
      received: {},
      changelogSeen: null,
      translation: DEFAULT_TRANSLATION,
      installCardDismissed: false,
      playedDays: [],
      sync: { code: null, version: 0, lastSyncedAt: null, localChangedAt: 0 },
      setSync: (s) => set((st) => ({ sync: { ...st.sync, ...s } })),
      dismissInstallCard: () => set({ installCardDismissed: true }),
      installIntroSeen: false,
      reminder: { on: false, hour: 8 },
      setReminder: (r) => set((s) => ({ reminder: { ...s.reminder, ...r } })),
      markInstallIntroSeen: () => set({ installIntroSeen: true }),
      setSenderName: (senderName) => set({ senderName }),
      recordReceived: (code, stars, now = new Date()) =>
        set((s) => (s.received[code] !== undefined ? s : { received: { ...s.received, [code]: stars }, streaks: recordCompletion(s.streaks, now), playedDays: addPlayedDay(s.playedDays, localDayKey(now)) })),
      addSent: (rec) => set((s) => ({ sent: [rec, ...s.sent.filter((x) => x.code !== rec.code)].slice(0, MAX_SENT_KEPT) })),
      forgetSent: (code) => set((s) => ({ sent: s.sent.filter((x) => x.code !== code) })),
      getPlayerId: () => {
        const existing = get().playerId;
        if (existing) return existing;
        const id = crypto.randomUUID();
        set({ playerId: id });
        return id;
      },
      recordResult: (number, stars, now = new Date()) =>
        set((s) => (s.stars[number] !== undefined ? s : { stars: { ...s.stars, [number]: stars }, streaks: recordCompletion(s.streaks, now), playedDays: addPlayedDay(s.playedDays, localDayKey(now)) })),
      markIntroSeen: () => set({ seenIntro: true }),
      markChangelogSeen: (date) => set((s) => (s.changelogSeen !== null && s.changelogSeen >= date ? s : { changelogSeen: date })),
      setTheme: (theme) => set({ theme }),
      setTranslation: (translation) => set({ translation: isTranslationCode(translation) ? translation : DEFAULT_TRANSLATION }),
    }),
    {
      name: "versedrill:v1",
      version: 2,
      storage: createJSONStorage(() => safeStorage),
      // v1 → v2 added playerId / sent / received; keep everyone's existing stars and streaks.
      migrate: (persisted) => migratePersisted(persisted) as never,
      // Players from before days were recorded keep their current streak's days (known exactly); older history can't be recovered.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as object) } as Store;
        return merged.playedDays.length === 0 ? { ...merged, playedDays: seedPlayedDays(merged.streaks) } : merged;
      },
    },
  ),
);

/** How many of each star count the player has earned, e.g. {5: 12, 4: 3}. */
export function starDistribution(stars: Record<number, number>): Record<number, number> {
  const out: Record<number, number> = {};
  for (const s of Object.values(stars)) out[s] = (out[s] ?? 0) + 1;
  return out;
}
